
import React, { useState } from "react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus } from "lucide-react";
import { 
  FundingRound,
  CapTableEntry
} from "@/lib/types";
import { calculateDilution } from "@/lib/capital-calculations";

interface FundingTrackerProps {
  fundingRounds: FundingRound[];
  setFundingRounds: React.Dispatch<React.SetStateAction<FundingRound[]>>;
  capTable: CapTableEntry[];
  setCapTable: React.Dispatch<React.SetStateAction<CapTableEntry[]>>;
}

export function FundingTracker({ 
  fundingRounds, 
  setFundingRounds,
  capTable,
  setCapTable
}: FundingTrackerProps) {
  const [newRound, setNewRound] = useState<Partial<FundingRound>>({});
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const handleAddRound = () => {
    if (!newRound.name || !newRound.preMoneyValuation || !newRound.amountRaised) {
      // In a real app, you'd show validation errors
      return;
    }

    // Calculate post-money valuation
    const postMoneyValuation = (newRound.preMoneyValuation || 0) + (newRound.amountRaised || 0);
    
    // Calculate share price based on pre-money valuation and existing shares
    const totalExistingShares = capTable.reduce((sum, entry) => sum + entry.sharesIssued, 0);
    const sharePrice = (newRound.preMoneyValuation || 0) / totalExistingShares;
    
    // Calculate new shares to issue
    const newSharesIssued = (newRound.amountRaised || 0) / sharePrice;
    
    // Calculate dilution
    const dilution = (newSharesIssued / (totalExistingShares + newSharesIssued)) * 100;

    // Create a new funding round entry
    const newEntry: FundingRound = {
      id: `${Date.now()}`, // Generate a unique ID
      name: newRound.name || "",
      date: newRound.date || new Date().toISOString().split('T')[0],
      preMoneyValuation: newRound.preMoneyValuation || 0,
      postMoneyValuation: postMoneyValuation,
      amountRaised: newRound.amountRaised || 0,
      leadInvestor: newRound.leadInvestor || "",
      newSharesIssued: Math.round(newSharesIssued),
      sharePrice: sharePrice,
      dilution: dilution,
      notes: newRound.notes || ""
    };

    // Add to funding rounds
    const updatedFundingRounds = [...fundingRounds, newEntry];
    setFundingRounds(updatedFundingRounds);
    
    // Update cap table to reflect the new investment
    // This is simplified - in a real app you'd add the new investor to the cap table
    const updatedCapTable = calculateDilution(capTable, newEntry);
    setCapTable(updatedCapTable);
    
    setIsAddDialogOpen(false);
    setNewRound({});
  };

  // Calculate totals
  const totalRaised = fundingRounds.reduce((sum, round) => sum + round.amountRaised, 0);
  const latestValuation = fundingRounds.length > 0 
    ? fundingRounds[fundingRounds.length - 1].postMoneyValuation 
    : 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Funding Tracker</CardTitle>
          <CardDescription>
            Track investment rounds and company valuation
          </CardDescription>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" /> Add Funding Round
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Funding Round</DialogTitle>
              <DialogDescription>
                Enter details about the new investment round.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Round Name</Label>
                  <Input
                    id="name"
                    value={newRound.name || ""}
                    onChange={(e) => setNewRound({...newRound, name: e.target.value})}
                    placeholder="e.g., Seed, Series A"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="date">Close Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newRound.date || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setNewRound({...newRound, date: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="preValuation">Pre-Money Valuation ($)</Label>
                  <Input
                    id="preValuation"
                    type="number"
                    value={newRound.preMoneyValuation || ""}
                    onChange={(e) => setNewRound({
                      ...newRound, 
                      preMoneyValuation: Number(e.target.value)
                    })}
                    placeholder="Pre-money valuation"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="amount">Amount Raised ($)</Label>
                  <Input
                    id="amount"
                    type="number"
                    value={newRound.amountRaised || ""}
                    onChange={(e) => setNewRound({
                      ...newRound, 
                      amountRaised: Number(e.target.value)
                    })}
                    placeholder="Amount raised"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="leadInvestor">Lead Investor</Label>
                <Input
                  id="leadInvestor"
                  value={newRound.leadInvestor || ""}
                  onChange={(e) => setNewRound({...newRound, leadInvestor: e.target.value})}
                  placeholder="Lead investor name"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newRound.notes || ""}
                  onChange={(e) => setNewRound({...newRound, notes: e.target.value})}
                  placeholder="Any additional details about this round"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddRound}>Add Funding Round</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-medium text-muted-foreground">Total Raised</div>
              <div className="text-2xl font-bold mt-1">${totalRaised.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-medium text-muted-foreground">Current Valuation</div>
              <div className="text-2xl font-bold mt-1">${latestValuation.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>
        
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Round</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Pre-Money</TableHead>
              <TableHead className="text-right">Raised</TableHead>
              <TableHead className="text-right">Post-Money</TableHead>
              <TableHead>Lead Investor</TableHead>
              <TableHead className="text-right">New Shares</TableHead>
              <TableHead className="text-right">Dilution</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fundingRounds.map((round) => (
              <TableRow key={round.id}>
                <TableCell className="font-medium">{round.name}</TableCell>
                <TableCell>{new Date(round.date).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">${round.preMoneyValuation.toLocaleString()}</TableCell>
                <TableCell className="text-right">${round.amountRaised.toLocaleString()}</TableCell>
                <TableCell className="text-right">${round.postMoneyValuation.toLocaleString()}</TableCell>
                <TableCell>{round.leadInvestor}</TableCell>
                <TableCell className="text-right">{round.newSharesIssued.toLocaleString()}</TableCell>
                <TableCell className="text-right">{round.dilution.toFixed(2)}%</TableCell>
              </TableRow>
            ))}
            {fundingRounds.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4 text-muted-foreground">
                  No funding rounds recorded yet. Add your first round to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
